import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Download, LogOut, Users, FileText, Settings, DollarSign, FileSignature, FilePen, Rocket, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import AdPlanDoc from "@/components/admin/AdPlanDoc";
import PricingDoc from "@/components/admin/PricingDoc";
import EngagementLetterDoc from "@/components/admin/EngagementLetterDoc";
import ProposalTab from "@/components/admin/ProposalTab";
import LaunchChecklist from "@/components/admin/LaunchChecklist";
import TemplatesTab from "@/components/admin/TemplatesTab";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "launch", label: "Launch", icon: Rocket },
  { id: "proposal", label: "Proposal", icon: FilePen },
  { id: "templates", label: "Templates", icon: Mail },
  { id: "pricing", label: "Pricing & Packaging", icon: DollarSign },
  { id: "letters", label: "Engagement Letter", icon: FileSignature },
  { id: "plan", label: "Advertising Plan", icon: FileText },
  { id: "tracking", label: "Tracking", icon: Settings },
];

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl bg-white border border-border p-8 shadow-lg"
      >
        <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center" aria-hidden="true">
          <svg viewBox="0 0 64 64" className="w-6 h-6">
            <path d="M40 22a12 12 0 1 0 0 20" stroke="#D97757" strokeWidth="7" fill="none" strokeLinecap="round" />
          </svg>
        </div>
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-navy">Clarus admin</h1>
        <p className="mt-1 text-sm text-navy/60">Sign in to view leads and your advertising plan.</p>
        <label htmlFor="admin-email" className="mt-6 block text-sm font-bold text-navy">Email</label>
        <input
          id="admin-email"
          data-testid="admin-email-input"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1.5 w-full rounded-xl border-2 border-navy/15 px-4 py-3 text-base text-navy focus:border-terracotta focus:outline-none"
        />
        <label htmlFor="admin-password" className="mt-4 block text-sm font-bold text-navy">Password</label>
        <input
          id="admin-password"
          data-testid="admin-password-input"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 w-full rounded-xl border-2 border-navy/15 px-4 py-3 text-base text-navy focus:border-terracotta focus:outline-none"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-700">{error}</p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-6 w-full rounded-full bg-terracotta px-6 py-3.5 text-base font-bold text-white transition-colors duration-200 hover:bg-terracotta-hover disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/admin/leads")
      .then(({ data }) => setLeads(data.leads))
      .catch(() => toast.error("Couldn't load leads"));
  }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "clarus-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (!leads) return <p className="text-navy/60">Loading leads…</p>;

  return (
    <div data-testid="leads-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy/60" data-testid="leads-count">{leads.length} lead{leads.length === 1 ? "" : "s"}</p>
        <button
          data-testid="export-csv-button"
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-bold text-navy hover:bg-navy hover:text-white transition-colors"
        >
          <Download className="w-4 h-4" aria-hidden="true" /> Export CSV
        </button>
      </div>
      {leads.length === 0 ? (
        <p className="mt-8 text-navy/60">No leads yet - they'll appear here when someone downloads the checklist or requests a quote.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-white">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="bg-navy text-white">
                {["Date", "Type", "Name", "Email", "Business", "Industry", "Xero status", "Message"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-t border-border" data-testid={`lead-row-${lead.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-navy/60">{new Date(lead.created_at).toLocaleDateString("en-AU")}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${lead.type === "quote" ? "bg-terracotta-light text-terracotta-hover" : "bg-navy/10 text-navy"}`}>
                      {lead.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-navy">{lead.name || "-"}</td>
                  <td className="px-4 py-3 text-navy/70">{lead.email}</td>
                  <td className="px-4 py-3 text-navy/70">{lead.business || "-"}</td>
                  <td className="px-4 py-3 text-navy/70">{lead.industry || "-"}</td>
                  <td className="px-4 py-3 text-navy/70">{lead.xero_status || "-"}</td>
                  <td className="px-4 py-3 text-navy/70 max-w-xs truncate" title={lead.message}>{lead.message || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TRACKING_FIELDS = [
  { key: "ga4_id", label: "GA4 Measurement ID", placeholder: "G-XXXXXXX" },
  { key: "google_ads_id", label: "Google Ads Conversion ID", placeholder: "AW-XXXXXXX" },
  { key: "google_ads_label", label: "Google Ads Conversion Label", placeholder: "abc123…" },
  { key: "meta_pixel_id", label: "Meta Pixel ID", placeholder: "1234567890" },
  { key: "gsc_verify", label: "Google Search Console verification token", placeholder: "content value from the meta tag" },
];

function TrackingTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/config").then(({ data }) => setConfig(data)).catch(() => setConfig({}));
  }, []);

  if (!config) return <p className="text-navy/60">Loading…</p>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", config);
      setConfig(data);
      toast.success("Tracking settings saved - tags load on next page visit.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="tracking-form" onSubmit={save} className="max-w-xl">
      <p className="text-sm text-navy/60 leading-relaxed">
        Paste the IDs from each platform here. The site injects GA4, Google Ads and Meta Pixel tags automatically once set - conversion events (bookings, checklist downloads, quote forms) fire on all three. Leave blank to keep a tag off.
      </p>
      {TRACKING_FIELDS.map((f) => (
        <div key={f.key} className="mt-5">
          <label htmlFor={`tracking-${f.key}`} className="block text-sm font-bold text-navy">{f.label}</label>
          <input
            id={`tracking-${f.key}`}
            data-testid={`tracking-${f.key.replace(/_/g, "-")}-input`}
            value={config[f.key] || ""}
            onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
            placeholder={f.placeholder}
            className="mt-1.5 w-full rounded-xl border-2 border-navy/15 bg-white px-4 py-3 text-base text-navy focus:border-terracotta focus:outline-none"
          />
        </div>
      ))}
      <button
        type="submit"
        data-testid="tracking-save-button"
        disabled={saving}
        className="mt-6 rounded-full bg-terracotta px-8 py-3 text-sm font-bold text-white hover:bg-terracotta-hover disabled:opacity-60 transition-colors"
      >
        {saving ? "Saving…" : "Save tracking settings"}
      </button>
    </form>
  );
}

export default function AdminPage() {
  const [auth, setAuth] = useState(null); // null=checking, false=logged out, object=logged in
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setAuth(data)).catch(() => setAuth(false));
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setAuth(false);
  }, []);

  if (auth === null) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-navy/60">Loading…</div>;
  }
  if (auth === false) {
    return (
      <>
        <Seo title="Admin | Clarus Partners" noindex />
        <LoginForm onLogin={setAuth} />
      </>
    );
  }

  return (
    <>
      <Seo title="Admin | Clarus Partners" noindex />
      <div data-testid="admin-dashboard" className="min-h-screen bg-background">
        <header className="bg-navy text-white no-print">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <p className="font-extrabold tracking-tight">Clarus Partners - Admin</p>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center gap-2 no-print">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                  tab === t.id ? "bg-navy text-white" : "bg-white border border-border text-navy/70 hover:text-navy"
                }`}
              >
                <t.icon className="w-4 h-4" aria-hidden="true" /> {t.label}
              </button>
            ))}
            {(tab === "plan" || tab === "pricing" || tab === "letters") && (
              <button
                data-testid="print-plan-button"
                onClick={() => window.print()}
                className="ml-auto inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-white hover:bg-terracotta-hover transition-colors"
              >
                <Download className="w-4 h-4" aria-hidden="true" /> Download as PDF
              </button>
            )}
          </div>

          <div className="mt-8 print-full">
            {tab === "leads" && <LeadsTab />}
            {tab === "launch" && <LaunchChecklist />}
            {tab === "proposal" && <ProposalTab />}
            {tab === "templates" && <TemplatesTab />}
            {tab === "pricing" && <PricingDoc />}
            {tab === "letters" && <EngagementLetterDoc />}
            {tab === "plan" && <AdPlanDoc />}
            {tab === "tracking" && <TrackingTab />}
          </div>
        </div>
      </div>
    </>
  );
}
